import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import MainLayout from "./MainLayout";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  Box,
  Button,
  TableSortLabel,
  Grid,
  InputAdornment,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Search, MailOutline } from "@mui/icons-material";
import TableChartIcon from "@mui/icons-material/TableChart";
import { TableBodySkeleton } from "../utils/TableSkeleton";
import {
  fetchUserEngagement,
  sendMail,
} from "../../redux/slices/userEngagementSlice";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import MultiSelectWithCheckbox from "../utils/MultiSelectWithCheckbox";
import { fetchUserOption } from "../../redux/slices/clientSlice";
import MultipleStopIcon from "@mui/icons-material/MultipleStop";
import RoleSelect from "../utils/RoleSelect";
import { ClickableTableLink } from "./ClickableTableLink";
import FilterBar2 from "./admin_dashboard/FilterBar2";
import { FY_YEAR_OPTIONS } from "../utils/FilterOptions";

const DAYS_FILTER_OPTIONS = [
  { label: "Last 7 Days", value: "7days" },
  { label: "Last 15 Days", value: "15days" },
  { label: "Last 30 Days", value: "30days" },
  { label: "More than 2 Months", value: "2months+" },
];

const roleOpt = [
  { label: "IC", value: 2 },
  { label: "BSP", value: 3 },
  { label: "DSP", value: 4 },
  { label: "BD", value: 5 },
  { label: "Sales Enabler", value: 6 },
  { label: "Dealer", value: 7 },
  { label: "Studio", value: 8 },
];

const UserEngagement = () => {
  const dispatch = useDispatch();
  const { engagement, count, status, userEngagementStatus } = useSelector(
    (state) => state.engagement
  );
  const counts = count || 0;

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mailMessage, setMailMessage] = useState("");
  const location = useLocation();
  const navState = location.state || {};

  const [dateFilter, setDateFilter] = useState([DAYS_FILTER_OPTIONS[0]]);
  const [roleId, setRoleId] = useState([]); 
  const [selectedFromDate, setSelectedFromDate] = useState(null);
  const [selectedToDate, setSelectedToDate] = useState(null);
  const [selectedUser, setSelectedUser] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  const [month, setMonth] = useState(() => {
    if (navState && navState.months) return navState.months;
    const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    const currentCalendarMonth = new Date().getMonth() + 1;
    const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
    return fullFYMonths.slice(0, currentMonthIndex + 1);
  });
  
  const [year, setYear] = useState(() => {
    if (navState.years?.length > 0) {
      const targetYrs = navState.years.map(String);
      return FY_YEAR_OPTIONS.filter((y) => targetYrs.includes(y.value));
    }
    const currentYrStr = String(new Date().getFullYear());
    return FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr);
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState({
    sort_by: "date",
    order: "desc",
  });
  const { userOption } = useSelector((state) => state.client);

  const isUsingDateRange = Boolean(selectedFromDate || selectedToDate);
  const hasValidFilters = isUsingDateRange || Boolean(year?.length > 0 && month?.length > 0);

  const isAllSelected =
    engagement?.length > 0 && selectedUsers.length === engagement.length;
  const isAnySelected = selectedUsers.length > 0;

  const [attachments, setAttachments] = useState([]);
  const [mailSubject, setMailSubject] = useState("");

  useEffect(() => {
    if (!hasValidFilters) {
      return;
    }
    dispatch(
      fetchUserEngagement({
        search: searchQuery,
        page,
        rowsPerPage,
        sort_by: sortBy.sort_by,
        sort_order: sortBy.order,
        dateFilter: dateFilter?.length > 0 ? dateFilter[0].value : "all",
        role: roleId?.map((r) => r.value),
        from: selectedFromDate,
        to: selectedToDate,
        users: selectedUser?.map((u) => u.value),
        month: month?.join(',') || "",
        year: year?.map((y) => y.value)?.join(',') || "",
      })
    );
  }, [
    searchQuery,
    page,
    rowsPerPage,
    sortBy,
    dateFilter,
    roleId,
    selectedFromDate,
    selectedToDate,
    selectedUser,
    month,
    year,
    hasValidFilters,
    dispatch,
  ]);

  useEffect(() => {
    dispatch(fetchUserOption());
  }, [dispatch]);

  const userOpt = useMemo(() => {
    return (
      userOption?.map((user) => ({
        value: user.id,
        label: `${user.name} ${user.last_name} (${user.emp_code})`,
      })) || []
    );
  }, [userOption]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = engagement.map((n) => n.user_id);
      setSelectedUsers(newSelecteds);
      return;
    }
    setSelectedUsers([]);
  };

  const handleRowClick = (user_id) => {
    const selectedIndex = selectedUsers.indexOf(user_id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedUsers, user_id);
    } else {
      newSelected = selectedUsers.filter((id) => id !== user_id);
    }

    setSelectedUsers(newSelected);
  };

  const isSelected = (user_id) => selectedUsers.indexOf(user_id) !== -1;

  const handleSendMail = async () => {
    if (!mailSubject.trim()) {
      alert("Please enter a mail subject");
      return;
    }

    if (!mailMessage.trim() && attachments.length === 0) {
      alert("Please enter a message or add attachments");
      return;
    }

    const MAX_SIZE = 3 * 1024 * 1024; // 3MB in bytes
    for (const file of attachments) {
      if (file.size > MAX_SIZE) {
        alert(`File "${file.name}" exceeds 3MB size limit.`);
        return;
      }
    }

    const base64Attachments = [];
    for (const file of attachments) {
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
      });
      base64Attachments.push({
        name: file.name,
        content: base64,
        type: file.type,
      });
    }

    dispatch(
      sendMail({
        user_ids: selectedUsers,
        subject: mailSubject,
        message: mailMessage,
        attachments: base64Attachments,
      })
    );
  };

  useEffect(() => {
    if (userEngagementStatus === "succeeded") {
      setMailMessage("");
      setMailSubject("");
      setAttachments([]);
      setSelectedUsers([]);
      setIsModalOpen(false);
    }
  }, [userEngagementStatus]);

  const handleDownloadExcel = () => {
    if (!engagement || engagement.length === 0) {
      console.log("No data to download.");
      return;
    }

    const dataForExcel = engagement.map((item, index) => {
      const date = item.last_quote_date ? new Date(item.last_quote_date) : null;
      return {
        "Sr. No": page * rowsPerPage + index + 1,
        "Emp Code": item.empCode,
        Name: item.name,
        Role: item.role_name,
        Email: item.email,
        Phone: item.phone,
        "Clients Count": item.client_count,
        "Quotations Count": item.quotation_count,
        "Activity Status": getDaysSinceDate(item.last_quote_date),
        "Activity Date": date
          ? date.toLocaleDateString("en-GB")
          : "N/A",
        "Activity Time": date
          ? date.toLocaleString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          })
          : "N/A",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "User Engagement");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(
      data,
      `User_Engagement_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const handleSort = (column) => {
    setSortBy((prev) => {
      if (prev.sort_by === column) {
        return {
          sort_by: column,
          order: prev.order === "asc" ? "desc" : "asc",
        };
      }
      return { sort_by: column, order: "asc" };
    });
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      // ["link", "image"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "align",
    "link",
    // "image",
  ];

  const columns = [
    {
      id: "select",
      label: "",
      align: "center",
      sortable: false,
      width: "50px",
    },
    {
      id: "sr_no",
      label: "Sr. No",
      align: "center",
      sortable: false,
      width: "80px",
    },
    { id: "user_type", label: "User Type", align: "center", sortable: false },
    {
      id: "empCode",
      label: "EMP Code/GST No.",
      align: "center",
      sortable: false,
    },
    { id: "name", label: "Name", align: "center", sortable: true },
    { id: "role_id", label: "Role", align: "center", sortable: true },
    { id: "email", label: "Email", align: "center", sortable: false },
    { id: "phone", label: "Phone", align: "center", sortable: false },
    {
      id: "client_count",
      label: "Total Clients",
      align: "center",
      sortable: true,
    },
    {
      id: "quotation_count",
      label: "Total Quotes",
      align: "center",
      sortable: true,
    },
    {
      id: "last_quote_date",
      label: "Activity Status",
      align: "center",
      sortable: true,
    },
    {
      id: "last_quote_date",
      label: "Activity Date",
      align: "center",
      sortable: true,
    },
    {
      id: "last_quote_time",
      label: "Activity Time",
      align: "center",
      sortable: false,
    },
  ];

  const getDaysSinceDate = (dateString) => {
    if (!dateString) return "N/A";

    const lastQuoteDate = new Date(dateString);
    const today = new Date();
    lastQuoteDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffTime = Math.abs(today.getTime() - lastQuoteDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "1 day ago";
    return `${diffDays} days ago`;
  };

  const filterConfigs = [
    { id: "days", label: "Filter by Days", options: DAYS_FILTER_OPTIONS, selected: dateFilter, isSingle: true },
    { id: "user", label: "User", options: userOpt || [], selected: selectedUser },
    { id: "role", label: "Role", options: roleOpt || [], selected: roleId },
    { id: "year", label: "Year (FY)", options: FY_YEAR_OPTIONS, selected: year },
  ];

  const handleMonthChange = (newMonths) => {
    setMonth(newMonths);
    if (newMonths?.length > 0 && year?.length === 0) {
      const currentYrStr = String(new Date().getFullYear());
      setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
    }
    setPage(0);
  };

  const handleFilterChange = (filterId, nextSelected) => {
    switch (filterId) {
      case "days": {
        const single = Array.isArray(nextSelected) && nextSelected.length > 0
          ? [nextSelected[nextSelected.length - 1]]
          : [];
        setDateFilter(single);
        break;
      }
      case "user": setSelectedUser(nextSelected); break;
      case "role": setRoleId(nextSelected); break;
      case "year":
        setYear(nextSelected);
        if (nextSelected.length === 0) {
          setMonth([]);
        } else if (month?.length === 0) {
          const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
          const currentCalendarMonth = new Date().getMonth() + 1;
          const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
          const currentYrStr = String(new Date().getFullYear());
          const hasCurrentYear = nextSelected.some((y) => String(y.value) === currentYrStr);
          if (!hasCurrentYear) {
            setMonth(fullFYMonths);
          } else {
            setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));
          }
        }
        break;
      default: break;
    }
    setPage(0);
  };

  const handleClearFilters = () => {
    setDateFilter([DAYS_FILTER_OPTIONS[0]]);
    setSelectedUser([]);
    setRoleId([]);
    setSelectedFromDate(null);
    setSelectedToDate(null);
    setSearchQuery("");
    setSortBy({ sort_by: "date", order: "desc" });
    setPage(0);

    const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    const currentCalendarMonth = new Date().getMonth() + 1;
    const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
    setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));

    const currentYrStr = String(new Date().getFullYear());
    setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
  };

  return (
    <MainLayout title={"User's Activity"}>
      <Box sx={{ p: 0 }}>
        <FilterBar2
          filters={filterConfigs}
          onFilterChange={handleFilterChange}
          selectedMonth={month}
          setSelectedMonth={handleMonthChange}
          searchTerm={searchQuery}
          setSearchTerm={(val) => { setSearchQuery(val); setPage(0); }}
          selectedFromDate={selectedFromDate}
          setSelectedFromDate={(val) => { setSelectedFromDate(val); setPage(0); }}
          selectedToDate={selectedToDate}
          setSelectedToDate={(val) => { setSelectedToDate(val); setPage(0); }}
          onClearFilters={handleClearFilters}
          actions={
            <div className="flex items-center gap-2">
              {isAnySelected && (
                <Button
                  variant="contained"
                  startIcon={<MailOutline />}
                  onClick={() => setIsModalOpen(true)}
                  sx={{
                    textTransform: "none",
                    bgcolor: "#d32f2f",
                    py: "2px",
                    px: "14px",
                    borderRadius: 12,
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                    "&:hover": { bgcolor: "#9a0007" },
                  }}
                >
                  Mail ({selectedUsers.length})
                </Button>
              )}

              <Button
                variant="contained"
                startIcon={<TableChartIcon />}
                onClick={handleDownloadExcel}
                sx={{
                  textTransform: "none",
                  bgcolor: "#2e7d32",
                  py: "2px",
                  px: "14px",
                  borderRadius: 12,
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: "#1b5e20" },
                }}
              >
                Excel
              </Button>
            </div>
          }
        />

        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "0px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Table stickyHeader>
            <TableHead sx={{ background: "#04121f", color: "white" }}>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                      minWidth: column.width || "auto",
                      background: "#04121f",
                    }}
                  >
                    {column.id === "select" ? (
                      <Checkbox
                        color="primary"
                        indeterminate={
                          selectedUsers.length > 0 &&
                          selectedUsers.length < engagement?.length
                        }
                        checked={isAllSelected}
                        onChange={handleSelectAllClick}
                        inputProps={{
                          "aria-label": "select all desserts",
                        }}
                        sx={{
                          color: "white",
                          "&.Mui-checked": { color: "white" },
                          "&.MuiCheckbox-indeterminate": { color: "white" },
                        }}
                      />
                    ) : column.sortable ? (
                      <TableSortLabel
                        active={sortBy.sort_by === column.id}
                        direction={
                          sortBy.sort_by === column.id ? sortBy.order : "desc"
                        }
                        onClick={() => handleSort(column.id)}
                        hideSortIcon={false}
                        sx={{
                          color: "white !important",
                          cursor: "pointer",
                          "& .MuiTableSortLabel-icon": {
                            opacity: 1,
                            color: "white !important",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: column.align,
                          }}
                        >
                          {column.label}
                        </Box>
                      </TableSortLabel>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: column.align,
                        }}
                      >
                        {column.label}
                      </Box>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!hasValidFilters ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    No data found
                  </TableCell>
                </TableRow>
              ) : engagement?.length > 0 ? (
                engagement?.map((item, index) => {
                  const isItemSelected = isSelected(item.user_id);
                  const labelId = `enhanced-table-checkbox-${item.user_id}`;

                  return (
                    <TableRow
                      hover
                      onClick={() => handleRowClick(item.user_id)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={item.user_id}
                      selected={isItemSelected}
                      sx={{
                        cursor: "pointer",
                        "&.Mui-selected": {
                          backgroundColor: "rgba(2, 136, 209, 0.1)",
                        },
                      }}
                    >
                      <TableCell padding="checkbox" align="center">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          inputProps={{
                            "aria-labelledby": labelId,
                          }}
                        />
                      </TableCell>

                      <TableCell id={labelId} align="center">
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell align="center">{item.user_type}</TableCell>
                      <TableCell align="center">{item.empCode}</TableCell>
                      <TableCell align="center"><ClickableTableLink path="/admin/userlist" displayValue={item.name} searchValue={item.empCode} /></TableCell>
                      <TableCell align="center">{item.role_name}</TableCell>
                      <TableCell align="center">{item.email}</TableCell>
                      <TableCell align="center">{item.phone}</TableCell>
                      <TableCell align="center">{item.client_count}</TableCell>
                      <TableCell align="center">
                        {item.quotation_count}
                      </TableCell>
                      <TableCell align="center">
                        {getDaysSinceDate(item.last_quote_date)}
                      </TableCell>
                      <TableCell align="center">
                        {item.last_quote_date
                          ? new Date(item.last_quote_date).toLocaleDateString(
                            "en-GB"
                          )
                          : "N/A"}
                      </TableCell>
                      <TableCell align="center">
                        {item.last_quote_date
                          ? new Date(item.last_quote_date).toLocaleString(
                            "en-GB",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            }
                          )
                          : "N/A"}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (status === "pending" || userEngagementStatus === "pending") ? (
                <TableBodySkeleton cols={columns.length} rows={10} />
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    No data found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={hasValidFilters ? counts : 0}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50, 100, counts]}
          sx={{ borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px" }}
        />
      </Box>

      {/*  Mail Modal Dialog */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="mail-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          id="mail-dialog-title"
          sx={{ bgcolor: "#04121f", color: "white" }}
        >
          Compose email for selected users!
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <p style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
              Selected Users:{" "}
              <span style={{ color: "#d32f2f" }}>{selectedUsers.length}</span>
            </p>
          </Box>

          <TextField
            fullWidth
            label="Mail Subject"
            value={mailSubject}
            onChange={(e) => setMailSubject(e.target.value)}
            margin="normal"
            required
          />

          <InputLabel
            htmlFor="mail-message-textarea"
            sx={{ mb: 1, fontWeight: "bold", mt: 2 }}
          >
            Message Content
          </InputLabel>

          {/* <ReactQuill
            theme="snow"
            value={mailMessage}
            onChange={setMailMessage}
            style={{ height: "150px", marginBottom: "50px" }}
          /> */}

          <ReactQuill
            theme="snow"
            value={mailMessage}
            onChange={setMailMessage}
            modules={quillModules}
            formats={quillFormats}
            style={{ height: "250px", marginBottom: "50px" }}
          />

          <Box sx={{ mt: 4 }}>
            <InputLabel sx={{ mb: 1, fontWeight: "bold" }}>
              Attachments (Max 3MB per file - PDF, Excel, Images)
            </InputLabel>
            <input
              type="file"
              multiple
              accept=".pdf,.xlsx,.xls,.jpg,.jpeg,.png,.gif"
              onChange={(e) => {
                const files = Array.from(e.target.files);
                const MAX_SIZE = 3 * 1024 * 1024; // 3MB

                // Check file sizes
                const oversized = files.find((file) => file.size > MAX_SIZE);
                if (oversized) {
                  alert(`File "${oversized.name}" exceeds 3MB size limit.`);
                  e.target.value = ""; // Clear input
                  return;
                }

                setAttachments(files);
              }}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />

            {/* Show selected files */}
            {attachments.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption">Selected files:</Typography>
                <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
                  {attachments.map((file, index) => (
                    <li key={index} style={{ fontSize: "12px" }}>
                      {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      <button
                        type="button"
                        onClick={() => {
                          setAttachments(
                            attachments.filter((_, i) => i !== index)
                          );
                        }}
                        style={{
                          marginLeft: "10px",
                          background: "transparent",
                          border: "none",
                          color: "red",
                          cursor: "pointer",
                        }}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsModalOpen(false)}
            color="#04121f"
            sx={{ textTransform: "none", borderRadius: "4px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendMail}
            variant="contained"
            color="primary"
            disabled={!mailMessage.trim()}
            startIcon={<MailOutline />}
            sx={{
              textTransform: "none",
              bgcolor: "#04121f",
              "&:hover": { bgcolor: "#01579b" },
              borderRadius: "4px",
            }}
          >
            Send Mail
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};

export default UserEngagement;
